import moment from 'moment';
import React, { JSX, useState } from 'react';

import { IStartToEndDateRanges } from '~/src/dash/components/calendar/common';
import { ISelectOption } from '~/src/dash/components/dropdowns/common';
import { FilterRowV2 } from '~/src/dash/components/filters/FilterRowV2';
import { IconV2 } from '~/src/dash/components/icons/IconV2';
import { IconText } from '~/src/dash/components/IconText';
import { SearchResultsText } from '~/src/dash/components/input/common';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractDefaultActiveColumnKeys,
  extractVisibleColumns,
  IListViewTableColumnInfo,
  IListViewTableRow,
  Order,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { ColumnSelector } from '~/src/dash/components/tables/ListViewTable/linked_components/ColumnSelector';
import {
  borderRadius,
  Col,
  Cols,
  Panel,
  Panels,
  Row,
  Rows,
  spacing,
  Text,
  TextInput,
  usePublicaTranslation,
} from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateNoData } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import { DateRangeRow } from '~/src/dash/components/filters/DateRangeRow';
import { IAllRulesConfig } from '~/src/dash/views/inventory-rules/AllRules/config';
import { IInventoryRules, IInventoryRulesUiParams } from '~/src/dash/views/inventory-rules/common';
import { track } from '~/src/dash/analytics/analytics';

interface IAllRulesProps {
  inventoryRules: IInventoryRules[];
  visibleRows: IListViewTableRow[];
  fetchInventoryRules: (params: IInventoryRulesUiParams) => void;
  cfg: IAllRulesConfig;
  updateCfg: (params: IInventoryRulesUiParams) => void;
  totalRows: number;
  isActionPending: boolean;
  loadState: LoadingState;
  handleClickOpenDrawer: (row: IListViewTableRow) => void;
  handleFilterAccept: (value: Set<string | number>, key: string) => void;
  handleClearFilters: () => void;
  filters: Record<string, ISelectOption[]>;
  selectedFilters: Record<string, Set<string | number>>;
  filtersLoadState: LoadingState;
  handleApplyDateRanges: (ranges: IStartToEndDateRanges) => void;
  startToEndDateRanges: IStartToEndDateRanges;
  areDateRangesInvalid: boolean;
  handleOnChangeSearch: (value: string) => void;
  handleChangePage: (event: React.MouseEvent | null, page: number) => void;
  handleChangeRowsPerPage: (event: React.ChangeEvent<HTMLInputElement>) => void;
  handleOnClickColumnHeader: (column: IListViewTableColumnInfo) => void;
  orderBy: string;
  order: Order;
  page: number;
  rowsPerPage: RowsPerPage;
}

export const AllRules = ({
  inventoryRules,
  visibleRows,
  cfg,
  isActionPending,
  totalRows,
  loadState,
  handleClickOpenDrawer,
  handleFilterAccept,
  handleClearFilters,
  filters,
  selectedFilters,
  filtersLoadState,
  handleApplyDateRanges,
  startToEndDateRanges,
  areDateRangesInvalid,
  handleOnChangeSearch,
  handleChangePage,
  handleChangeRowsPerPage,
  handleOnClickColumnHeader,
  orderBy,
  order,
  page,
  rowsPerPage,
}: IAllRulesProps): JSX.Element => {
  const { isSuperUser, session, user } = useBaseStore();
  const { cypressClassName, defaultState } = cfg;
  const { t } = usePublicaTranslation();

  const [activeColumnKeys, setActiveColumnKeys] = useState<string[]>(
    defaultState.activeColumnKeys || extractDefaultActiveColumnKeys(cfg.table.columns),
  );

  const handleChangeActiveColumns = (keys: string[]): void => {
    setActiveColumnKeys(keys);
  };

  const handleAcceptActiveColumns = (keys: string[]): void => {
    track('Inventory Rules - Active Columns', { isSuperUser, session, columns: keys }, user.user);
    setActiveColumnKeys(keys);
  };

  const handleOnClickRow = (row: IListViewTableRow): void => {
    handleClickOpenDrawer(row);
    track('Inventory Rules - Open Drawer', { isSuperUser, session }, user.user);
  };

  return (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
      paddingTop={spacing.none}
    >
      {
        <Panels max vertical relative paddingTop={spacing.base} style={{ rowGap: spacing.medium }}>
          {inventoryRules.length === 0 && loadState === LoadingStateNoData && (
            <Panel>
              <Rows>
                <Row
                  marginTop={spacing.small}
                  style={{
                    padding: `${spacing.extraSmall} ${spacing.small}`,
                    backgroundColor: 'rgba(93, 102, 250, 0.08)',
                    width: 'fit-content',
                    borderRadius: borderRadius.base,
                  }}
                >
                  <IconText
                    mini
                    color={publicaColors.darkGray}
                    gap={spacing.extraSmall}
                    icon='light-bulb'
                    iconColor={publicaColors.blue}
                    text={`${t('NoDataToDisplay')}. ${t('UploadCSVToSharedS3RoleTooltip')}`}
                    style={{
                      padding: 'none',
                      marginTop: '1px',
                    }}
                  />
                </Row>
              </Rows>
            </Panel>
          )}
          <Panel highlighted>
            <Rows>
              <Row noPadding style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
                <Rows>
                  <Row noPadding style={{ borderBottom: `1px solid ${publicaColors.lightGray}` }}>
                    <DateRangeRow
                      cypressParentName={cfg.cypressClassName}
                      selectedRanges={startToEndDateRanges}
                      handleApplyDateRanges={handleApplyDateRanges}
                      hasError={areDateRangesInvalid}
                      defaultTimezone=''
                      handleApplyTimezone={() => {
                        // No timezone handling needed
                      }}
                    />
                  </Row>
                  <Row noPadding>
                    <FilterRowV2
                      cypressParentName={cfg.cypressClassName}
                      enableClear={true}
                      filtersConfig={cfg.filters}
                      filtersToOptionsMap={filters}
                      handleApplySelect={handleFilterAccept}
                      handleClearFilters={handleClearFilters}
                      selectedFilters={selectedFilters}
                      loadState={filtersLoadState}
                    />
                  </Row>
                </Rows>
              </Row>
            </Rows>
          </Panel>
          <Panel highlighted largeHorizontalPadding verticalPadding>
            {/* TABLE */}
            <Rows>
              <Row style={{ padding: '8px 0px' }}>
                <Cols style={{ justifyContent: 'space-between' }}>
                  <Col>
                    <Cols
                      wrap
                      gap={spacing.medium}
                      style={{
                        alignItems: 'center',
                        rowGap: spacing.small,
                      }}
                    >
                      <Col>
                        <TextInput
                          enableClear
                          handleOnChange={handleOnChangeSearch}
                          msDelay={500}
                          placeholder={t('Search')}
                          prefix='search'
                        />
                      </Col>
                      <Col>
                        <SearchResultsText
                          numRowsDisplayed={visibleRows.length}
                          numTotalRows={totalRows}
                        />
                      </Col>
                    </Cols>
                  </Col>
                  <Col>
                    <Cols>
                      <Col>
                        <Cols
                          style={{
                            padding: `${spacing.extraSmall} ${spacing.small}`,
                            backgroundColor: publicaColors.lighterBlue,
                            borderRadius: borderRadius.small,
                            alignItems: 'center',
                            gap: spacing.extraSmall,
                          }}
                        >
                          <Col displayFlex>
                            <IconV2 name='available' color={publicaColors.teal} />
                          </Col>
                          <Col>
                            <Text extraSmall>
                              {t('CSVLastUpdated')}:&nbsp;
                              {inventoryRules.length !== 0
                                ? moment(inventoryRules[0].CreatedAt).format('YYYY-MM-DD HH:mm:ss')
                                : 'N/A'}
                            </Text>
                          </Col>
                        </Cols>
                      </Col>
                      <Col>
                        <ColumnSelector
                          // simple multi select props
                          preselectedColumnKeys={cfg.defaultState.activeColumnKeys}
                          resetActiveColumnKeys={cfg.columnSelector.resetKeys}
                          columnsList={extractVisibleColumns(
                            cfg.table.columns,
                            activeColumnKeys || [],
                            isSuperUser,
                          )}
                          columnsHandleOnChangeOption={handleChangeActiveColumns}
                          columnsHandleOnAccept={handleAcceptActiveColumns}
                        />
                      </Col>
                    </Cols>
                  </Col>
                </Cols>
              </Row>
              <Row noHorizontalPadding>
                <ListViewTable
                  activeColumnKeys={activeColumnKeys}
                  columns={cfg.table.columns}
                  isExpanded={defaultState.isTableExpanded}
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  handleOnClickColumnHeader={handleOnClickColumnHeader}
                  handleOnClickRow={handleOnClickRow}
                  isActionPending={isActionPending}
                  isReadAuthorized={true}
                  isSuperUser={isSuperUser}
                  isWriteAuthorized={false}
                  loadState={loadState}
                  order={order}
                  orderBy={orderBy}
                  page={page}
                  rows={visibleRows}
                  rowsPerPage={rowsPerPage}
                  totalRowCount={totalRows}
                  className={cypressClassName}
                />
              </Row>
            </Rows>
          </Panel>
        </Panels>
      }
    </Panels>
  );
};
